import { useQuery } from "@tanstack/react-query";
import { GetFooter } from "../api/queries";
import React, {  useState } from "react";
import packageJson from "../../../package.json";
import { Link } from "react-router-dom";


export default function Footer() {  

  const currentYear = new Date().getFullYear();
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [showCookieConsent, setShowCookieConsent] = useState<boolean>(false);

  const acceptCookies = () => {
    // Set cookie consent for 1 year
    document.cookie = "cookieConsent=accepted; max-age=31536000; path=/; SameSite=Strict";
    setShowCookieConsent(false);
  };

  const checkCookieConsent = () => {
    const consent = document.cookie
      .split('; ')
      .find(row => row.startsWith('cookieConsent='));
    return consent ? consent.split('=')[1] === 'accepted' : false;
  };

  // Check cookie consent on component mount
  React.useEffect(() => {
    const hasConsent = checkCookieConsent();
    if (!hasConsent) {
      // Show consent banner after a short delay
      const timer = setTimeout(() => {
        setShowCookieConsent(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const useFooter = useQuery({
    queryKey: ["footer"],
    queryFn: GetFooter,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return (
    <footer id="footer" className="sticky">
      <hr className="h-[3px] mx-0 bg-harvey-red border-0 lg:mx-0" />
      <div className="w-full flex lg:items-center lg:justify-center bg-black">
        <div className="mt-1 lg:mt-4 w-full text-white">
          <div className="flex flex-col mb-1">
            <div className={`flex lg:flex-row flex-col items-center lg:px-6 xl:px-12 2xl:px-16 ${useFooter.isFetched && useFooter.data?.results && useFooter.data.results.length > 0
              ? 'lg:items-start'
              : 'lg:items-center justify-center'
              }`}>
              <img
                className={`hidden xl:block max-w-xs my-4 xl:mb-0 ${useFooter.isFetched && useFooter.data?.results && useFooter.data.results.length > 0
                  ? 'xl:ml-0'
                  : 'mx-auto'
                  }`}
                src="https://hncnzwww.blob.core.windows.net/www/img/HNfooterlogo-all-white.svg"
                alt="Harvey Norman Logo"
              />

              {useFooter.isFetched && useFooter.data?.results && useFooter.data.results.length > 0 && (
                <>
                  {/* Desktop Version */}
                  <div className="hidden lg:flex lg:flex-row items-stretch w-full justify-around lg:mt-4">
                    {useFooter.data.results.map((item, index) => (
                      <div key={item.url || index} className="flex flex-col items-start text-gray-200">
                        <h1 className="uppercase text-base lg:text-xl font-semibold lg:mb-1">
                          {item.name}
                        </h1>
                        <ul className="text-left my-1">
                          {item.sublinks.map((subitem, subindex) => {
                            const linkId = `${item.name}-${subitem.name}`;
                            const isHovered = hoveredLink === linkId;
                            const isDimmed = hoveredLink !== null && hoveredLink !== linkId;
                            const isExternal = /^https?:\/\//.test(subitem.url);

                            return (
                              <li
                                className={`py-1 transition-all duration-300 ease-in-out hover:scale-105 ${isDimmed ? 'opacity-50' : 'opacity-100'
                                  } ${isHovered ? 'text-white' : 'text-gray-200'}`}
                                key={subitem.url || subindex}
                                onMouseEnter={() => setHoveredLink(linkId)}
                                onMouseLeave={() => setHoveredLink(null)}
                              >
                                {isExternal ? (
                                  <a href={subitem.url}
                                  {...(subitem.openinnewwindow && { target: "_blank", rel: "noopener noreferrer" })}
                                   className="transition-all duration-300 relative inline-block">
                                    <span className="relative z-10">{subitem.name}</span>
                                    <span className={`absolute inset-0 bg-gradient-to-r from-harvey-red/20 to-harvey-blue/20 rounded-md transition-opacity duration-300 -z-10 transform origin-left ${isHovered ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></span>
                                  </a>
                                ) : (
                                  <Link to={subitem.url}
                                  {...(subitem.openinnewwindow && { target: "_blank", rel: "noopener noreferrer" })}
                                  className="transition-all duration-300 relative inline-block">
                                    <span className="relative z-10">{subitem.name}</span>
                                    <span className={`absolute inset-0 bg-gradient-to-r from-harvey-red/20 to-harvey-blue/20 rounded-md transition-opacity duration-300 -z-10 transform origin-left ${isHovered ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></span>
                                  </Link>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>

                  {/* Mobile Version - Clean List Design */}
                  <div className="lg:hidden w-full px-4 pt-8 space-y-4">
                    {useFooter.data.results.map((item, index) => (
                      <div key={item.url || index} className="w-full">
                        <h2 className="text-white font-bold text-lg mb-2 pb-1 border-b border-harvey-red/30">
                          {item.name}
                        </h2>
                        <div className="space-y-2">
                          {item.sublinks.map((subitem, subindex) => {
                            const linkId = `mobile-${item.name}-${subitem.name}`;
                            const isHovered = hoveredLink === linkId;
                            const isDimmed = hoveredLink !== null && hoveredLink !== linkId;
                            const isExternal = /^https?:\/\//.test(subitem.url);

                            return (
                              <div
                                key={subitem.url || subindex}
                                className={`transition-all duration-300 ${isDimmed ? 'opacity-60' : 'opacity-100'
                                  }`}
                                onMouseEnter={() => setHoveredLink(linkId)}
                                onMouseLeave={() => setHoveredLink(null)}
                              >
                                {isExternal ? (
                                  <a
                                    href={subitem.url}
                                    {...(subitem.openinnewwindow && { target: "_blank", rel: "noopener noreferrer" })}
                                    className={`flex items-center justify-between p-3 rounded-xl transition-all duration-300 ${isHovered
                                      ? 'bg-gradient-to-r from-harvey-red/15 to-harvey-blue/15 text-white border-l-4 border-harvey-red'
                                      : 'bg-transparent text-gray-300 hover:bg-gray-800/50 border-l-4 border-transparent'
                                      }`}
                                  >
                                    <span className="font-medium">{subitem.name}</span>
                                    <svg className={`w-5 h-5 transition-transform duration-300 ${isHovered ? 'translate-x-1 text-harvey-red' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </a>
                                ) : (
                                  <Link
                                    to={subitem.url}
                                    {...(subitem.openinnewwindow && { target: "_blank", rel: "noopener noreferrer" })}
                                    className={`flex items-center justify-between p-3 rounded-xl transition-all duration-300 ${isHovered
                                      ? 'bg-gradient-to-r from-harvey-red/15 to-harvey-blue/15 text-white border-l-4 border-harvey-red'
                                      : 'bg-transparent text-gray-300 hover:bg-gray-800/50 border-l-4 border-transparent'
                                      }`}
                                    onClick={() => setHoveredLink(null)}
                                  >
                                    <span className="font-medium">{subitem.name}</span>
                                    <svg className={`w-5 h-5 transition-transform duration-300 ${isHovered ? 'translate-x-1 text-harvey-red' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </Link>
                                               )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <hr className="border-gray-600 mt-4" />

            <div className="w-full text-center text-gray-500 py-1 text-xs">
              <p>
                © 2007-{currentYear} Harvey Norman Stores (NZ) Pty Limited. All rights reserved. | Private Bag 94035 SAMC, Wiri Auckland 2240 | All prices exclusive of GST | v{process.env.REACT_APP_VERSION || packageJson.version}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Consent Banner */}
      {showCookieConsent && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-sm border-t border-gray-600 p-4 z-50 transform transition-transform duration-500 ease-in-out">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex-1 text-white text-sm">
              <p>
                🍪 This website uses cookies to enhance your browsing experience and provide personalized content.
                By continuing to use this site, you consent to our use of cookies.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={acceptCookies}
                className="bg-harvey-red hover:bg-harvey-red/90 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200"
              >
                Accept Cookies
              </button>
              <Link
                to="/privacy-policy"
                className="text-gray-300 hover:text-white px-4 py-2 text-sm underline transition-colors duration-200"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
